import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { SessionService } from '@services/session';
import { AuthService } from '@services/auth.service';
import { SKIP_AUTH } from './http-context.tokens';

// Estado compartido para coordinar un único refresh cuando varias peticiones
// fallan con 401 al mismo tiempo. Las peticiones concurrentes esperan al mismo
// refresh en curso en lugar de disparar uno por cada una.
let isRefreshing = false;
let refreshedToken$ = new BehaviorSubject<string | null>(null);

export const tokenInterceptorFn: HttpInterceptorFn = (req, next) => {
    const sessionService = inject(SessionService);
    const authService = inject(AuthService);

    if (req.context.get(SKIP_AUTH)) {
        return next(req);
    }

    const token = sessionService.userToken();
    const authReq = token ? addToken(req, token) : req;

    return next(authReq).pipe(
        catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status === 401 && token) {
                return handle401(req, next, sessionService, authService);
            }
            return throwError(() => error);
        })
    );
};

function addToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
}

function handle401(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    sessionService: SessionService,
    authService: AuthService
): Observable<HttpEvent<unknown>> {
    // Sin refresh_token no hay nada que refrescar: se cierra la sesión.
    if (!sessionService.refreshToken()) {
        sessionService.endSession();
        return throwError(() => new HttpErrorResponse({ status: 401 }));
    }

    // Ya hay un refresh en curso: esperar al nuevo token y reintentar.
    // Si el refresh falla, refreshedToken$ emite error y estas peticiones también
    // erroran (no se cuelgan). Su error se propaga al componente; el cierre de
    // sesión lo dispara el refresh primario, no cada petición en espera.
    if (isRefreshing) {
        return refreshedToken$.pipe(
            filter((newToken): newToken is string => newToken !== null),
            take(1),
            switchMap((newToken) => next(addToken(req, newToken)))
        );
    }

    isRefreshing = true;
    // Subject nuevo por cada ciclo de refresh: evita reutilizar un token retenido
    // de un ciclo anterior (un BehaviorSubject conserva su último valor).
    refreshedToken$ = new BehaviorSubject<string | null>(null);

    return authService.refreshToken().pipe(
        // catchError ANTES del switchMap: solo captura el fallo del refresh.
        // El logout debe ocurrir únicamente cuando no se puede renovar la sesión,
        // no cuando la petición reintentada falla por sus propias reglas de negocio
        // (p.ej. create-checkin devuelve 404 por foto inválida). Esos errores deben
        // propagarse al componente para mostrarse como mensaje, sin cerrar sesión.
        catchError((error) => {
            isRefreshing = false;
            refreshedToken$.error(error); // libera a las peticiones en espera
            sessionService.endSession();
            return throwError(() => error);
        }),
        switchMap((newToken) => {
            isRefreshing = false;
            refreshedToken$.next(newToken);
            return next(addToken(req, newToken));
        })
    );
}
